import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { FieldColorPicker, FieldNumberArray, FieldSlider, FormField } from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import IndentsIcon from './indents.svg?react'
import type { TableFormBrandType } from './tableTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.table.style, {
  content: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  header: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.headerColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  divider: {
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  indent: {
    padding: genBrandField({
      as: FieldNumberArray,
      description: <IndentsIcon />,
      label: t('elements.table.schema.padding'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.table.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const TableFormStyle: TableFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])
  return (
    <Padding bottom gap sides>
      <Typography weight='medium'>{t('elements.table.tags.content')}</Typography>
      <FormField config={schema.content.backgroundColor} />
      <FormField config={schema.header.backgroundColor} />
      <FormField config={schema.divider.borderColor} />
      <FormField config={schema.divider.borderWidth} />

      <Typography weight='medium'>{t('elements.table.tags.indent')}</Typography>
      <FormField config={schema.indent.padding} />

      <Typography weight='medium'>{t('style.effects')}</Typography>
      <FormField config={schema.effects.boxShadow} />

      <TileButton
        description={t('uiKit.button.richTextDescription')}
        icon='otherRight'
        name='showFontForm'
        onClick={handleSetTypography}
      >
        <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
        {isAnyEdited(edited.font) && <BrandTagEdited />}
      </TileButton>
    </Padding>
  )
}

export default React.memo(TableFormStyle) as typeof TableFormStyle
